import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useSubcontractorProfile } from '@/contexts/SubcontractorProfileContext';
import { supabase } from '@/lib/supabaseClient';
import { getDemoSession, getDemoSetting, renderThankYou } from '@/lib/demo';
import { fetchSceneDefs, PART_META, PART_SUB_ROUTES, PART_PUNCHLINES, isEyesUpScene, sceneImage, sceneTheme } from '@/lib/demoScenes';
import { useDemoAccess } from '@/contexts/DemoAccessContext';
import useLivePartPrices from '@/hooks/useLivePartPrices';
import ProductDemoModal from '@/components/subcontractor/ProductDemoModal';
import ForecastProjector from '@/components/shared/ForecastProjector';
import { ArrowRight, Heart, MonitorPlay, Play, Sparkles, X } from 'lucide-react';

// ── DemoDeckBar ──────────────────────────────────────────────────────────────
// The attendee half of the GUIDED module presentation. Renders ONLY for demo
// identities (profile.is_demo). When the presenter pushes a scene, every
// attendee device shows a full-screen takeover — part chip, punchline, what to
// try — and "Explore it" drops them INSIDE that module (PART_SUB_ROUTES) to
// move around freely. Eyes-up scenes point at the projector instead. A small
// pulsing pill recalls the takeover at any time, so it never blocks the app
// once dismissed. Scenes come from the admin-authored demo_scene_defs
// (built-in script as fallback); a scene with an attached product demo shows a
// Watch button that opens the same full-screen ProductDemoModal the profile
// page uses.
// Realtime is the primary transport: a websocket subscription on the session
// row flips every phone in the same instant the presenter pushes a module.
// Polling stays as the safety net (venue wifi, dropped sockets) at a relaxed
// cadence rather than being the mechanism.
// Fallback poll while realtime is the primary transport. Phones drop the
// websocket the moment they lock or background, and a 15s poll made scene
// changes feel broken in the room (owner report, 21 Sep) — 4s keeps a dead
// socket unnoticeable, and waking the page catches up instantly.
const POLL_MS = 4000;

const DemoDeckBar = () => {
  useLivePartPrices(); // part chips show the admin's Parts figures, not the code fallbacks
  const { profile } = useSubcontractorProfile();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  // Pricing switch rides the scene-lock context: when the presenter runs the
  // room without pricing, part chips read "name · van" with no £ figure.
  const { showPricing } = useDemoAccess();
  const [scenes, setScenes] = useState(null); // null = still loading the deck
  const [scene, setScene] = useState(null);
  const [sessionLive, setSessionLive] = useState(true);
  const [open, setOpen] = useState(true);
  const [attachedDemo, setAttachedDemo] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [forecastOpen, setForecastOpen] = useState(false);
  const [demoEnded, setDemoEnded] = useState(false);
  const [magicPing, setMagicPing] = useState(null);
  const [previewFailed, setPreviewFailed] = useState({});
  const [sessionId, setSessionId] = useState(null);
  // Goodbye personalisation: the session TITLE (the prospect's company, set by
  // the admin at session start) must be captured while the session is still
  // readable — RLS hides ended sessions — and the admin-edited thank-you
  // template comes from demo_settings. Both fail soft to defaults.
  const [sessionTitle, setSessionTitle] = useState('');
  const [thankYouTpl, setThankYouTpl] = useState(null);
  const lastScene = useRef(null);
  const endedRef = useRef(false);
  const pingTimer = useRef(null);
  // Presence + local recap: the row id we heartbeat, and seconds-per-route
  // accumulated on-device so the goodbye screen can speak even after the
  // server-side identity is purged.
  const activityRowId = useRef(null);
  const routeSince = useRef(null);
  const localRecap = useRef({});
  const location = useLocation();

  const bankRouteTime = () => {
    if (!routeSince.current) return;
    const { route, at } = routeSince.current;
    localRecap.current[route] = (localRecap.current[route] || 0) + (Date.now() - at) / 1000;
    routeSince.current = null;
  };

  const code = (() => {
    try { return localStorage.getItem('demo_session_code'); } catch { return null; }
  })();

  useEffect(() => {
    if (!profile?.is_demo || !code) return undefined;
    let stale = false;
    fetchSceneDefs().then((list) => { if (!stale) setScenes(list); });
    getDemoSetting('thank_you_template').then((v) => { if (!stale && v) setThankYouTpl(v); });
    return () => { stale = true; };
  }, [profile?.is_demo, code]);

  useEffect(() => {
    if (!profile?.is_demo || !code) return undefined;
    let stop = false;

    const applySession = (s) => {
      if (stop || endedRef.current) return;
      // A call that didn't get through says NOTHING about the session — keep
      // the last known scene and wait for the next tick. Ending the demo here
      // wiped the device's session marker on a 4G blip and left the attendee
      // unguided on a plain dashboard for the rest of the room (owner
      // walkthrough, 29 Sep). Only the server's own not_found/ended ends it.
      if (!s || s.status === 'error') return;
      if (s?.title) setSessionTitle(s.title);
      if (s.status === 'not_found' || s.status === 'ended') {
        // The presenter ended the demo (or wiped it) — this identity has been
        // purged server-side. Close the dash behind a grounded thank-you and
        // clear the demo marker so a later scan starts fresh.
        endedRef.current = true;
        setSessionLive(false);
        setOpen(false);
        setDemoOpen(false);
        setForecastOpen(false);
        setDemoEnded(true);
        try { localStorage.removeItem('demo_session_code'); } catch { /* private mode */ }
        return;
      }
      setSessionLive(true);
      if (s.id) setSessionId(s.id);
      const next = Math.max(0, s.current_scene ?? 0);
      setScene(next);
      // A pushed scene takes the screen; the attendee dismisses it into the app.
      if (lastScene.current !== next) {
        lastScene.current = next;
        setOpen(true);
      }
    };

    const tick = async () => {
      if (endedRef.current) return;
      applySession(await getDemoSession(code));
    };

    // Primary: realtime — the presenter's push lands on every phone at once.
    // The channel also carries the MAGIC MOMENT: an allocation inserted for
    // THIS demo identity pings a celebration in front of the room.
    const channel = supabase
      .channel(`demo-sync-${code}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'demo_sessions', filter: `code=eq.${code}` },
        (payload) => applySession(payload.new))
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'crew_allocations', filter: `subcontractor_id=eq.${profile.id}` },
        (payload) => {
          const row = payload.new;
          if (!row?.date) return;
          setMagicPing({ date: row.date });
          clearTimeout(pingTimer.current);
          pingTimer.current = setTimeout(() => setMagicPing(null), 9000);
        })
      .subscribe();

    // Safety net: polling in case the socket drops (venue wifi, phone lock).
    tick();
    const id = setInterval(tick, POLL_MS);
    // A locked/backgrounded phone kills the websocket AND throttles timers —
    // catch up the moment the attendee looks back at the screen.
    const wake = () => { if (document.visibilityState !== 'hidden') tick(); };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    return () => {
      stop = true;
      clearInterval(id);
      clearTimeout(pingTimer.current);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
      supabase.removeChannel(channel);
    };
  }, [profile?.is_demo, code, profile?.id]);

  // Presence + telemetry: log a row when this device enters a page, heartbeat
  // it while we stay, and bank the duration locally for the goodbye recap.
  useEffect(() => {
    if (!profile?.is_demo || !sessionId || endedRef.current) return undefined;
    const route = location.pathname;
    bankRouteTime();
    routeSince.current = { route, at: Date.now() };
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('demo_activity')
          .insert({ session_id: sessionId, demo_sub_id: profile.id, route })
          .select('id')
          .maybeSingle();
        if (!cancelled) activityRowId.current = data?.id || null;
      } catch { /* presence is best-effort */ }
    })();
    const hb = setInterval(() => {
      if (activityRowId.current) {
        supabase.from('demo_activity')
          .update({ last_seen_at: new Date().toISOString() })
          .eq('id', activityRowId.current)
          .then(() => {}, () => {});
      }
    }, 5000);
    return () => { cancelled = true; clearInterval(hb); bankRouteTime(); };
  }, [profile?.is_demo, profile?.id, sessionId, location.pathname]);

  // current_scene indexes the ordered active deck — clamp against the loaded
  // list so a mid-session edit that shrinks the deck can't point past the end.
  const idx = scenes && scenes.length > 0 && scene !== null
    ? Math.max(0, Math.min(scenes.length - 1, scene))
    : null;
  const current = idx === null ? null : scenes[idx];

  // Resolve the attached product-demo film for scenes carrying a demo_key.
  useEffect(() => {
    const key = current?.demo_key;
    if (!key) { setAttachedDemo(null); setDemoOpen(false); return undefined; }
    let stale = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('product_demos')
          .select('id, key, title, blurb, file_path')
          .eq('key', key)
          .eq('enabled', true)
          .maybeSingle();
        if (!stale) setAttachedDemo(error ? null : (data || null));
      } catch { if (!stale) setAttachedDemo(null); }
    })();
    return () => { stale = true; };
  }, [current?.demo_key]);

  // The forecast scene mirrors onto the attendee's own device: auto-opens the
  // moment the presenter lands on it, closes when the deck moves on. Keyed off
  // part_key; the title regex only covers a DB predating the part_key column.
  const isForecastScene = !!current
    && (current.part_key ? current.part_key === 'numbers' : /number|forecast/i.test(current.title || ''));
  useEffect(() => {
    setForecastOpen(isForecastScene);
  }, [isForecastScene]);

  const finishDemo = async () => {
    try { await supabase.auth.signOut(); } catch { /* already gone */ }
    // The crew sign-in lives at '/' on this host ('/login' only exists on the
    // Runmark site and 404s here — found in the 14 Oct audit).
    window.location.replace('/');
  };

  // Ended: cover the dash entirely — the identity behind it no longer exists.
  if (demoEnded) {
    // The recap speaks from on-device timing — the server identity is already
    // purged, so nothing here depends on a query succeeding.
    bankRouteTime();
    const routeToPart = Object.fromEntries(Object.entries(PART_SUB_ROUTES).map(([k, r]) => [r, k]));
    const partSeconds = {};
    Object.entries(localRecap.current).forEach(([route, secs]) => {
      const pk = routeToPart[route];
      if (pk) partSeconds[pk] = (partSeconds[pk] || 0) + secs;
    });
    const explored = Object.keys(partSeconds);
    const totalMin = Math.max(1, Math.round(Object.values(localRecap.current).reduce((a, b) => a + b, 0) / 60));
    const top = explored.sort((a, b) => partSeconds[b] - partSeconds[a])[0];
    const firstName = (profile?.first_name || '').trim();
    const mailSubject = encodeURIComponent(`Pilot conversation — ${profile?.full_name || 'demo attendee'}`);

    return (
      // Same mobile-browser sizing as the takeover: dvh so the Finish button
      // clears the browser's bottom bar, my-auto so overflow scrolls from the
      // top instead of clipping (items-center would cut the heading off).
      <div className="fixed inset-x-0 top-0 z-[120] flex h-screen flex-col overflow-y-auto overscroll-contain bg-slate-950 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-center text-white supports-[height:100dvh]:h-[100dvh]">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mx-auto my-auto w-full max-w-md">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/15 text-primary">
            <Heart className="h-8 w-8" />
          </div>
          <h1 className="text-3xl font-semibold" style={{ color: '#ffffff' }}>
            Thank you{firstName ? `, ${firstName}` : ''}.
          </h1>
          {/* Admin-edited goodbye (Product Demos → Thank-you screen); {company}
              is the session title, captured while the session was readable. */}
          <p className="mt-3 text-lg font-semibold leading-relaxed" style={{ color: 'rgba(255,255,255,0.95)' }}>
            {renderThankYou(thankYouTpl, sessionTitle)}
          </p>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Your demo access has been securely removed — nothing from this session remains.
          </p>

          {explored.length > 0 && (
            <div className="mt-5 rounded-2xl border border-white/15 bg-white/5 p-4 text-left">
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-primary">Your {totalMin} minute{totalMin === 1 ? '' : 's'} inside the machine</p>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-200">
                <li>• You ran a live subcontractor account on a real crew mirror</li>
                <li>• You explored {explored.length} part{explored.length === 1 ? '' : 's'} of the platform yourself</li>
                {top && PART_META[top] && (
                  <li>• Longest inside <span className="font-bold text-white">{PART_META[top].name}</span> — {Math.max(1, Math.round(partSeconds[top] / 60))}m</li>
                )}
              </ul>
            </div>
          )}

          <a
            href={`mailto:hello@parcelink.org?subject=${mailSubject}`}
            className="mt-6 block rounded-2xl bg-primary px-6 py-4 text-sm font-black uppercase tracking-widest text-white shadow-lg hover:opacity-90"
          >
            Book the pilot conversation →
          </a>
          <p className="mt-3 text-xs text-slate-400">A recap email is on its way to your inbox.</p>
          <button
            type="button"
            onClick={finishDemo}
            className="mt-5 rounded-xl border border-white/20 px-6 py-3 text-sm font-bold uppercase tracking-wider text-slate-300 hover:text-white"
          >
            Finish
          </button>
        </motion.div>
      </div>
    );
  }

  if (!profile?.is_demo || !code || !sessionLive || idx === null || !current) return null;

  const eyesUp = isEyesUpScene(current);
  // A scene may land Explore somewhere other than its part's default page
  // (e.g. Kit your crew is Operations, but lives on /uniform, not the planner).
  const route = eyesUp ? null : current.sub_route || PART_SUB_ROUTES[current.part_key] || null;
  // The admin-authored punchline (demo_scene_defs.punchline, 20261251) wins;
  // without one, a custom-route scene is not its part's flagship moment — its
  // own title is the punch (the part punchline belongs to the default-page
  // scene).
  const punchline = current.punchline
    || (!eyesUp && !current.sub_route && PART_PUNCHLINES[current.part_key])
    || current.title;
  // Slide image per SCENE (20261254): the admin's choice of image, motion
  // and container from the designer; defaults reproduce the old behaviour
  // (the page's stored capture, floating, in the device frame).
  const slide = sceneImage(current);
  const t = sceneTheme(current);
  const hintLines = (current.hint || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const hintLead = hintLines.filter((l) => !l.startsWith('•')).join(' ');
  const hintBullets = hintLines.filter((l) => l.startsWith('•')).map((l) => l.replace(/^•\s*/, ''));
  const part = PART_META[current.part_key];

  const explore = () => {
    setOpen(false);
    if (route) navigate(route);
  };

  const stagger = (i) => (prefersReducedMotion ? {} : {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: 0.1 + i * 0.1, duration: 0.3 },
  });

  return (
    <>
      {/* Collapsed: small pulsing float button, out of everything's way */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="deck-pill"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-24 right-3 z-[60] flex h-11 w-11 items-center justify-center rounded-full border border-primary/40 bg-slate-950/90 text-primary shadow-2xl backdrop-blur-md sm:bottom-6 sm:right-6"
            aria-label={`Show module ${idx + 1} of ${scenes.length}: ${current.title}`}
          >
            {!prefersReducedMotion && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/30" aria-hidden="true" />
            )}
            <Sparkles className="relative h-5 w-5" />
            <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-black text-white">
              {idx + 1}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Module takeover: the punchy intro, then into the module itself.
          Suppressed while the forecast projector owns the screen.
          Sized with dvh, not inset-0: attendees run this in the phone's
          BROWSER, where 100vh extends under the bottom URL/tab bar — the
          Explore button was being buried behind it (owner, 22 Sep). dvh
          tracks the visible viewport as browser chrome shows and hides;
          h-screen stays as the no-dvh fallback, and the card scrolls if a
          small screen still can't fit it. */}
      <AnimatePresence>
        {open && !forecastOpen && (
          <motion.div
            key={`module-${idx}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            className={`fixed inset-x-0 top-0 z-[80] flex h-screen flex-col overflow-y-auto overscroll-contain ${t.panel} px-5 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-12 backdrop-blur-xl supports-[height:100dvh]:h-[100dvh]`}
            role="dialog"
            aria-modal="true"
            aria-label={`Module ${idx + 1}: ${current.title}`}
          >
            <button
              onClick={() => setOpen(false)}
              className={`absolute right-4 top-4 rounded-full border p-2 ${t.close}`}
              aria-label="Dismiss module intro"
            >
              <X className="h-4 w-4" />
            </button>

            {/* my-auto (not justify-center on the parent) so the card CENTRES
                when it fits and scrolls from the TOP when it doesn't — flex
                centering inside a scroll container clips the top edge off
                overflowing content, unreachable by scrolling. */}
            <div className="mx-auto my-auto flex w-full max-w-lg flex-col">
              <motion.div {...stagger(0)} className="mb-2 flex flex-wrap items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-[12px] font-black text-white">{idx + 1}</span>
                {part && (
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-bold ${t.chip}`}>
                    {part.name} · {part.van}{showPricing ? ` · £${part.price}/crew` : ''}
                  </span>
                )}
                <span className={`ml-auto text-[11px] font-bold ${t.faint}`}>{idx + 1} / {scenes.length}</span>
              </motion.div>

              {/* text-white must be ON the h1: the global h1 rule applies
                  text-foreground directly, which beats the wrapper's inherited
                  white and rendered the punchline near-invisible in light
                  theme (owner-reported, 21 Sep). */}
              <motion.h1 {...stagger(1)} className={`text-[22px] font-semibold leading-[1.12] tracking-tight sm:text-3xl ${t.heading}`} style={{ textWrap: 'balance' }}>
                {punchline}
              </motion.h1>

              {/* Live screenshot of the actual module, captured from this app
                  by scripts/capture-demo-previews.mjs — not a stock mock.
                  Keyed per SCENE: a scene with its own route (Kit your crew →
                  /uniform) shows ITS page, never its part's default screen —
                  the availability planner on the uniform card confused the
                  room (owner, 22 Sep). Gently floats, like the walkthrough
                  films' device. */}
              {slide.src && !previewFailed[slide.src] && (
                <motion.div {...stagger(2)} className="mt-3 flex justify-center">
                  {/* Height rides the SMALL viewport unit: the frame gives
                      back a little on short screens so title, bullets and
                      both buttons always share the fold with it. Motion and
                      container come from the designer (sceneImage). */}
                  <motion.div
                    animate={prefersReducedMotion || slide.motion === 'still' ? {}
                      : slide.motion === 'drift' ? { scale: [1.04, 1.12], x: ['-1.5%', '1.5%'] }
                      : { y: [0, -6, 0] }}
                    transition={slide.motion === 'drift'
                      ? { duration: 18, repeat: Infinity, repeatType: 'reverse', ease: 'linear' }
                      : { duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
                    className={slide.frame === 'full'
                      ? `w-full max-h-[clamp(110px,24svh,210px)] overflow-hidden rounded-2xl border shadow-2xl sm:max-h-[260px] ${t.frame}`
                      : `max-h-[clamp(110px,22svh,190px)] overflow-hidden rounded-2xl border-2 shadow-2xl sm:max-h-[240px] ${t.frame}`}
                  >
                    <img
                      src={slide.src}
                      alt={`${PART_META[current.part_key]?.name || current.title} — live screen`}
                      className={slide.frame === 'full' ? 'block w-full object-cover' : 'block w-[160px] sm:w-[190px]'}
                      onError={() => setPreviewFailed((f) => ({ ...f, [slide.src]: true }))}
                    />
                  </motion.div>
                </motion.div>
              )}

              {/* Hints carry '•' remedy lines (migration 20261234): lead
                  sentence, then the pain-point remedies as a list. */}
              <motion.div {...stagger(2)} className="mt-3.5 text-left">
                {hintLead && (
                  <p className={`text-center text-[15px] leading-relaxed ${t.body}`}>{hintLead}</p>
                )}
                {hintBullets.length > 0 && (
                  <ul className="mx-auto mt-2.5 max-w-md space-y-1.5">
                    {hintBullets.map((b) => (
                      <li key={b} className={`flex items-start gap-2.5 text-[14px] leading-snug ${t.bullet}`}>
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>

              {/* Order is deliberate (owner, 1 Oct): the film sets the mark
                  first, exploration follows — and the Explore button sits
                  quieter beneath it. */}
              <motion.div {...stagger(3)} className="mt-5 space-y-2.5 sm:mt-8">
                {attachedDemo && (
                  <button
                    type="button"
                    onClick={() => setDemoOpen(true)}
                    className={`flex w-full items-center justify-center gap-2 rounded-2xl border px-4 py-2.5 text-xs font-bold ${t.ghostBtn}`}
                  >
                    <Play className="h-4 w-4 shrink-0" aria-hidden="true" /> Watch: {attachedDemo.title}
                  </button>
                )}
                {route ? (
                  <button
                    onClick={explore}
                    className="mx-auto flex w-fit items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-white shadow-md transition-transform active:scale-[0.98]"
                  >
                    Explore {part?.name || current.title} <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => setOpen(false)}
                    className="mx-auto flex w-fit items-center justify-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-primary transition-transform active:scale-[0.98]"
                  >
                    <MonitorPlay className="h-4 w-4" /> Eyes on the big screen
                  </button>
                )}
                <div className="flex items-center justify-center gap-1 pt-1" aria-hidden="true">
                  {scenes.map((_, i) => (
                    <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === idx ? 'bg-primary' : t.dot}`} />
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {demoOpen && attachedDemo && (
        <ProductDemoModal demo={attachedDemo} subcontractorId={profile.id} onClose={() => setDemoOpen(false)} />
      )}

      {/* The numbers scene, mirrored to this device */}
      <AnimatePresence>
        {forecastOpen && <ForecastProjector onClose={() => setForecastOpen(false)} />}
      </AnimatePresence>

      {/* MAGIC MOMENT ping: the presenter just allocated THIS attendee live */}
      <AnimatePresence>
        {magicPing && (
          <motion.button
            key="magic-ping"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            onClick={() => { setMagicPing(null); navigate('/subcontractor/dashboard/routing'); }}
            className="fixed inset-x-3 top-4 z-[90] mx-auto max-w-md rounded-2xl border border-emerald-400/50 bg-emerald-600/95 px-4 py-3 text-left text-white shadow-2xl backdrop-blur-md"
            role="status"
            aria-live="assertive"
          >
            <p className="text-[11px] font-black uppercase tracking-[0.16em]">You&rsquo;re on the board</p>
            <p className="mt-0.5 text-sm leading-snug">You&rsquo;ve just been allocated for tomorrow — tap to see it on your Routing page.</p>
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default DemoDeckBar;
